<script setup lang="ts">
import { ref, watch } from 'vue'
import Badge from 'primevue/badge'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import ErrorState from '@/components/common/ErrorState.vue'
import type { InstalledAssetDetail } from '@/types/asset'
import { assetService } from '@/services/assetService'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
import {
  getWarrantyStatusBadgeClass,
  getWarrantyStatusLabel,
  getInstallationStatusBadgeClass,
  getInstallationStatusLabel,
  formatAssetDate,
  formatWarrantyDuration,
  formatRemainingDays
} from '@/utils/assetUtils'

const props = defineProps<{
  modelValue: boolean
  assetId: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const detail = ref<InstalledAssetDetail | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const fetchDetail = async () => {
  if (!props.assetId) return
  loading.value = true
  error.value = null
  detail.value = null
  try {
    detail.value = await assetService.getAssetById(props.assetId)
  } catch (err) {
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.modelValue, props.assetId],
  ([open]) => {
    if (open && props.assetId) fetchDetail()
  },
  { immediate: true }
)

const close = () => emit('update:modelValue', false)

const fieldEntries = (fieldValues: Record<string, string> | undefined) =>
  Object.entries(fieldValues || {}).filter(([, value]) => value !== undefined && value !== '')
</script>

<template>
  <ModalDefault :modelValue="modelValue" title="Installed Asset Details" width="max-w-3xl" @update:modelValue="close">
    <LoaderDefault v-if="loading" title="Loading asset details..." />

    <ErrorState v-else-if="error" :message="error" @retry="fetchDetail" />

    <div v-else-if="detail" class="space-y-6">
      <!-- Item summary -->
      <div class="flex items-start justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h4 class="text-lg font-semibold text-gray-800 dark:text-white">{{ detail.itemName || 'Unnamed Item' }}</h4>
          <p class="text-sm text-gray-500 dark:text-gray-400">Serial No: {{ detail.serialNumber || 'N/A' }}</p>
        </div>
        <div class="flex flex-col items-end gap-1.5">
          <Badge :value="getWarrantyStatusLabel(detail.warrantyStatus)" :class="getWarrantyStatusBadgeClass(detail.warrantyStatus)" />
          <Badge :value="getInstallationStatusLabel(detail.installationStatus)" :class="getInstallationStatusBadgeClass(detail.installationStatus)" />
        </div>
      </div>

      <!-- Warranty -->
      <section>
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Warranty</h5>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <p class="text-gray-500 dark:text-gray-400">Duration</p>
            <p class="font-medium">{{ formatWarrantyDuration(detail.warrantyYears, detail.warrantyMonths) }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Start Date</p>
            <p class="font-medium">{{ formatAssetDate(detail.warrantyStart) }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Expiry Date</p>
            <p class="font-medium">{{ formatAssetDate(detail.warrantyExpiry) }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Remaining</p>
            <p class="font-medium">{{ formatRemainingDays(detail.remainingDays) }}</p>
          </div>
        </div>
      </section>

      <!-- Tender details -->
      <section v-if="detail.tender">
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tender Details</h5>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p class="text-gray-500 dark:text-gray-400">Reference No</p>
            <p class="font-medium break-all">{{ detail.tender.referenceNo || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Tender ID</p>
            <p class="font-medium break-all">{{ detail.tender.t247Id || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Organization</p>
            <p class="font-medium">{{ detail.tender.organization || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Location</p>
            <p class="font-medium">{{ detail.tender.location || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Estimated Cost</p>
            <p class="font-medium">₹{{ detail.tender.estimatedCost?.toLocaleString('en-IN') || '0' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Deadline</p>
            <p class="font-medium">{{ formatAssetDate(detail.tender.deadline) }}</p>
          </div>
        </div>
      </section>

      <!-- Customer -->
      <section>
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Customer Details</h5>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p class="text-gray-500 dark:text-gray-400">Customer Name</p>
            <p class="font-medium">{{ detail.customerName || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Report Date</p>
            <p class="font-medium">{{ formatAssetDate(detail.reportDate) }}</p>
          </div>
        </div>
      </section>

      <!-- Engineer & Consignee -->
      <section>
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Engineer &amp; Consignee</h5>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p class="text-gray-500 dark:text-gray-400">Engineer</p>
            <p class="font-medium">{{ detail.engineer || 'Not Assigned' }}</p>
          </div>
          <div>
            <p class="text-gray-500 dark:text-gray-400">Consignee</p>
            <p class="font-medium">{{ detail.consigneeDetail?.name || detail.consignee || 'N/A' }}</p>
          </div>
          <div v-if="detail.consigneeDetail?.designation">
            <p class="text-gray-500 dark:text-gray-400">Designation</p>
            <p class="font-medium">{{ detail.consigneeDetail.designation }}</p>
          </div>
          <div v-if="detail.consigneeDetail?.number">
            <p class="text-gray-500 dark:text-gray-400">Contact Number</p>
            <p class="font-medium">{{ detail.consigneeDetail.number }}</p>
          </div>
          <div v-if="detail.consigneeDetail?.emailId">
            <p class="text-gray-500 dark:text-gray-400">Email</p>
            <p class="font-medium break-all">{{ detail.consigneeDetail.emailId }}</p>
          </div>
        </div>
      </section>

      <!-- Item configuration -->
      <section v-if="detail.itemConfiguration">
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Item Configuration</h5>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-2">Quantity: {{ detail.itemConfiguration.quantity || 'N/A' }}</p>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div v-for="[key, value] in fieldEntries(detail.itemConfiguration.fieldValues)" :key="key">
            <p class="text-gray-500 dark:text-gray-400 capitalize">{{ key.replace(/([A-Z])/g, ' $1') }}</p>
            <p class="font-medium break-all">{{ value }}</p>
          </div>
        </div>
      </section>

      <!-- Remarks -->
      <section v-if="detail.remarks">
        <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Remarks</h5>
        <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ detail.remarks }}</p>
      </section>
    </div>

    <template #footer>
      <button
        class="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        @click="close"
      >
        Close
      </button>
    </template>
  </ModalDefault>
</template>
